import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import type {
  GuidedTemplateDTO,
  WorkflowDTO,
  PopulationType,
  SamplingMethod,
} from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { useAtom } from 'jotai'
import {
  ArrowLeft,
  Pencil,
  Globe,
  Lock,
  TrendingUp,
  PlayCircle,
  Settings2,
  User,
  Database,
  Camera,
} from 'lucide-react'
import { toast } from 'sonner'
import { useDocumentTitle } from 'usehooks-ts'
import { workspaceIdAtom } from '@/stores/workspaceStore'
import { authClient } from '@/lib/auth-client'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { BrowserProfileSelector } from '@/components/browser-profile/BrowserProfileSelector'
import { TemplateDetailContent } from '@/components/compliance/TemplateDetailContent'
import type { VersionHistoryItem } from '@/components/compliance/TemplateVersionHistory'
import {
  POPULATION_TYPE_LABELS,
  SAMPLING_METHOD_LABELS,
  SAMPLING_METHOD_DESCRIPTIONS,
} from '@/components/compliance/test-types/types'
import MainHeader from '@/components/layout/MainHeader'
import { Main } from '@/components/main'

function TemplateDetailPage() {
  const { templateId } = Route.useParams()
  const search = Route.useSearch<{ via?: string; canClone?: string }>()
  const [workspaceId] = useAtom(workspaceIdAtom)
  const [versionHistoryEnabled, setVersionHistoryEnabled] = useState(false)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  const isPartnerEntry = search?.via === 'partner'
  const partnerCanClone = search?.canClone === '1'

  // State for Create Test flow
  const [samplingDialogOpen, setSamplingDialogOpen] = useState(false)
  const [browserProfileDialogOpen, setBrowserProfileDialogOpen] =
    useState(false)
  const [samplingConfig, setSamplingConfig] = useState<{
    method: SamplingMethod
    sampleSize: number
    populationId?: string
  }>({
    method: 'RANDOM',
    sampleSize: 25,
  })
  const [selectedBrowserProfileId, setSelectedBrowserProfileId] =
    useState<string>('__fresh__')
  const [, setSelectedSteelProfileId] = useState<string>('')

  // Get current user for permission check
  const { data: session } = authClient.useSession()
  const currentUserId = session?.user?.id

  // Fetch template for marketplace view (no submissions)
  const templateQuery = useQuery({
    ...trpc.guidedTemplate.getTemplate.queryOptions({
      id: templateId,
    }),
    enabled: !!templateId,
  })

  const template = templateQuery.data as unknown as GuidedTemplateDTO
  // template?.author===null 表示系统模板
  const isSystemTemplate = template?.author === null

  // Fetch version history (lazy loaded when panel is opened)
  const versionHistoryQuery = useQuery({
    ...trpc.guidedTemplate.getVersionHistory.queryOptions({
      templateId,
    }),
    enabled: !!templateId && versionHistoryEnabled,
  })

  // Build workflow from executionConfig for WORKFLOW type templates
  const workflow = useMemo(() => {
    if (
      template?.templateType !== 'WORKFLOW' ||
      !template?.executionConfig?.steps
    ) {
      return undefined
    }
    return {
      steps: template.executionConfig.steps,
      params: template.executionConfig.params || {},
      successCriteria: template.executionConfig.successCriteria,
    } as WorkflowDTO
  }, [template])

  // Publish/unpublish mutation
  const publishMutation = useMutation({
    ...trpc.guidedTemplate.updateTemplate.mutationOptions(),
    onSuccess: (result) => {
      const isPublic = result.isPublic
      toast.success(
        isPublic
          ? 'Template published successfully'
          : 'Template unpublished successfully'
      )
      // Invalidate the template query to refresh the data
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getTemplate.queryKey({ id: templateId }),
      })
    },
    onError: (error) => {
      toast.error(`Failed to update template: ${error.message}`)
    },
  })

  const handleTogglePublish = () => {
    if (!template) return
    const newIsPublic = !template.isPublic
    publishMutation.mutate({
      id: templateId,
      isPublic: newIsPublic,
      changeNote: newIsPublic ? 'Published template' : 'Unpublished template',
    })
  }

  // Fetch available populations for sampling dialog
  const populationsQuery = useQuery(trpc.population.list.queryOptions({}))

  // Import template version mutation (Create Test)
  const importMutation = useMutation({
    ...trpc.guidedTemplate.importTemplateVersion.mutationOptions(),
    onSuccess: (result) => {
      toast.success('Test created successfully')
      setSamplingDialogOpen(false)
      setBrowserProfileDialogOpen(false)
      // Navigate to the test detail page
      navigate({
        to: '/workspaces/$workspaceId/tests/$testId',
        params: { workspaceId: workspaceId!, testId: result.id },
      })
    },
    onError: (error) => {
      toast.error(`Failed to create test: ${error.message}`)
    },
  })

  // Get control IDs from template mappings
  const templateControlIds = useMemo(() => {
    const mappings = template?.controlMappings as
      | { controlDefId: string }[]
      | undefined
    return mappings?.map((m) => m.controlDefId) || []
  }, [template])
  const canCreateTestFromTemplateDetail = Boolean(
    template &&
    templateControlIds.length > 0 &&
    !(template.isPublic && !template.docId)
  )

  // Filter populations by the template's required population type
  type PopulationItem = {
    id: string
    name: string
    type: string
    recordCount?: number
  }
  const filteredPopulations = useMemo(() => {
    const data = populationsQuery.data as
      | { items: PopulationItem[] }
      | undefined
    const items = data?.items || []
    const requiredType = template?.samplingConfig?.populationType
    if (!requiredType) return items
    return items.filter((pop: PopulationItem) => pop.type === requiredType)
  }, [populationsQuery.data, template?.samplingConfig?.populationType])

  // Handle Create Test button click
  const handleCreateTestClick = () => {
    if (!template) return

    const requiresSampling = template.samplingConfig?.requiresPopulation
    const requiresBrowserProfile =
      template.templateType === 'WORKFLOW' ||
      template.templateType === 'STATIC_SCREENSHOT'

    if (requiresSampling) {
      // Show sampling configuration dialog
      setSamplingConfig({
        method:
          (template.samplingConfig?.recommendedMethod as SamplingMethod) ||
          'RANDOM',
        sampleSize: template.samplingConfig?.minimumSampleSizeRecommended || 25,
        populationId: undefined,
      })
      setSamplingDialogOpen(true)
    } else if (requiresBrowserProfile) {
      // Show browser profile configuration dialog
      setSelectedBrowserProfileId('__fresh__')
      setSelectedSteelProfileId('')
      setBrowserProfileDialogOpen(true)
    } else {
      // Direct import for simple templates
      handleCreateTest()
    }
  }

  // Execute the import/create test
  const handleCreateTest = (populationId?: string) => {
    if (!template?.currentVersionId) {
      toast.error('Template version not available. Please try again.')
      return
    }

    if (templateControlIds.length === 0) {
      toast.error(
        'This template has no associated controls. Cannot create test.'
      )
      return
    }

    importMutation.mutate({
      templateVersionId: template.currentVersionId,
      controlDefIds: templateControlIds,
      populationId,
    })
  }

  // Confirm sampling config and create test
  const handleConfirmSampling = () => {
    handleCreateTest(samplingConfig.populationId)
  }

  // Confirm browser profile and create test
  const handleConfirmBrowserProfile = () => {
    handleCreateTest(samplingConfig.populationId)
  }

  // Partner "Use Template" clone mutation (when navigated from partner card)
  const cloneMutation = useMutation({
    ...trpc.guidedTemplate.cloneTemplate.mutationOptions(),
    onSuccess: (data) => {
      toast.success('Template cloned successfully')
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.listWorkspaceTemplates.queryKey(),
      })
      // Optional: stay on page for consistency with card behavior
    },
    onError: (error) => {
      toast.error(`Failed to clone template: ${error.message}`)
    },
  })

  // Handle browser profile change
  const handleBrowserProfileChange = (
    profileId: string,
    steelProfileId: string
  ) => {
    setSelectedBrowserProfileId(profileId)
    setSelectedSteelProfileId(steelProfileId)
  }

  useDocumentTitle(
    template?.doc?.topic?.title || template?.title || 'Template Details'
  )

  return (
    <Main>
      <MainHeader />
      <div className='mx-auto box-content flex max-w-[1008px] flex-col px-6 pb-12'>
        {/* Back Button */}
        <div className='mb-6'>
          <Button
            variant='ghost'
            size='sm'
            className='gap-2'
            onClick={() => window.history.back()}
          >
            <ArrowLeft className='h-4 w-4' />
            Back to Templates
          </Button>
        </div>

        {/* Header */}
        <div className='mb-8'>
          <div className='mb-2 flex items-center justify-between gap-4'>
            <h1 className='text-3xl font-semibold'>
              {template?.doc?.topic?.title ||
                template?.title ||
                'Template Details'}
            </h1>
            {/* Owner actions - show if user is creator; hidden for partner entry to keep read-only UX */}
            {!isPartnerEntry &&
              canWorkspaceWrite &&
              template &&
              (template.publisherUserId === currentUserId ||
                !template.publisherUserId ||
                template?.clonedByUserId === currentUserId) && (
                <div className='flex items-center gap-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    className='gap-2'
                    onClick={handleTogglePublish}
                    disabled={publishMutation.isPending}
                  >
                    {template.isPublic ? (
                      <>
                        <Lock className='h-4 w-4' />
                        Unpublish
                      </>
                    ) : (
                      <>
                        <Globe className='h-4 w-4' />
                        Publish
                      </>
                    )}
                  </Button>
                  {!isSystemTemplate && canWorkspaceWrite && (
                    <Link
                      to='/workspaces/$workspaceId/templates/$templateId/edit'
                      params={{ workspaceId: workspaceId!, templateId }}
                      preload={false}
                    >
                      <Button variant='outline' size='sm' className='gap-2'>
                        <Pencil className='h-4 w-4' />
                        Edit Template
                      </Button>
                    </Link>
                  )}
                </div>
              )}
          </div>
          {/* Stats */}
          {canWorkspaceWrite && template && (
            <div className='flex flex-wrap items-center gap-3'>
              {template.isPublic ? (
                <Badge variant='default' className='gap-1'>
                  <Globe className='h-3 w-3' />
                  Public
                </Badge>
              ) : (
                <Badge variant='secondary' className='gap-1'>
                  <Lock className='h-3 w-3' />
                  Private
                </Badge>
              )}
              {/* TODO: Re-enable vanity metrics when we have significant usage
              <div className='text-muted-foreground flex items-center gap-3 text-sm'>
                <div className='flex items-center gap-1'>
                  <TrendingUp className='h-3.5 w-3.5' />
                  <span className='text-foreground font-medium'>
                    {template.usageCount || 0}
                  </span>
                  <span>{(template.usageCount || 0) === 1 ? 'clone' : 'clones'}</span>
                </div>
                <div className='flex items-center gap-1'>
                  <PlayCircle className='h-3.5 w-3.5' />
                  <span className='text-foreground font-medium'>
                    {(template.totalExecutions || 0) >= 1000
                      ? `${((template.totalExecutions || 0) / 1000).toFixed(1)}k`
                      : template.totalExecutions || 0}
                  </span>
                  <span>{(template.totalExecutions || 0) === 1 ? 'run' : 'runs'}</span>
                </div>
              </div>
              */}
            </div>
          )}
        </div>

        {/* Template Details */}
        <div>
          <TemplateDetailContent
            template={template}
            submissions={undefined}
            isLoading={templateQuery.isLoading}
            showStartTest={false}
            showSubmissions={false}
            workflow={workflow}
            versionHistory={
              versionHistoryQuery.data as VersionHistoryItem[] | undefined
            }
            isLoadingVersionHistory={versionHistoryQuery.isLoading}
            onVersionHistoryOpen={() => setVersionHistoryEnabled(true)}
            // Partner entry: replace Create Test with Use Template (clone); hide if not cloneable
            onCreateTest={
              isPartnerEntry
                ? partnerCanClone
                  ? () => cloneMutation.mutate({ templateId })
                  : undefined
                : canWorkspaceWrite && canCreateTestFromTemplateDetail
                  ? handleCreateTestClick
                  : undefined
            }
            isCreatingTest={
              isPartnerEntry
                ? cloneMutation.isPending
                : importMutation.isPending
            }
            createTestLabel={isPartnerEntry ? 'Use Template' : undefined}
          />
        </div>
      </div>

      {/* Sampling Configuration Dialog */}
      <Dialog open={samplingDialogOpen} onOpenChange={setSamplingDialogOpen}>
        <DialogContent className='max-w-[740px] min-w-[740px]'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Settings2 className='h-5 w-5 text-status-blue' />
              Configure Sampling
            </DialogTitle>
            <DialogDescription>
              This template requires population sampling. Configure how evidence
              should be collected.
            </DialogDescription>
          </DialogHeader>

          {template && (
            <div className='space-y-4 py-4'>
              {/* Template Info */}
              <div className='bg-muted rounded-md p-3'>
                <div className='text-sm font-medium'>
                  {template.title ||
                    template.doc?.topic?.title ||
                    'Untitled Template'}
                </div>
                {template.samplingConfig?.populationType && (
                  <div className='text-muted-foreground mt-1 flex items-center gap-1 text-xs'>
                    <Database className='h-3 w-3' />
                    Requires{' '}
                    {POPULATION_TYPE_LABELS[
                      template.samplingConfig.populationType as PopulationType
                    ] || template.samplingConfig.populationType}{' '}
                    population
                  </div>
                )}
              </div>

              {/* Population Selection */}
              <div className='space-y-2'>
                <Label>Population</Label>
                <Select
                  value={samplingConfig.populationId || 'none'}
                  onValueChange={(value) =>
                    setSamplingConfig((prev) => ({
                      ...prev,
                      populationId: value === 'none' ? undefined : value,
                    }))
                  }
                >
                  <SelectTrigger className='w-full'>
                    <SelectValue placeholder='Select a population...' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='none'>
                      <span className='text-muted-foreground'>
                        No population selected (configure later)
                      </span>
                    </SelectItem>
                    {filteredPopulations.map((pop: PopulationItem) => (
                      <SelectItem key={pop.id} value={pop.id}>
                        <div className='flex items-center gap-2'>
                          <span>{pop.name}</span>
                          <Badge variant='secondary' className='text-[10px]'>
                            {pop.recordCount ?? 0} items
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {filteredPopulations.length === 0 && (
                  <p className='text-muted-foreground text-xs'>
                    No matching populations found. You can create one later.
                  </p>
                )}
              </div>

              {/* Sampling Method */}
              <div className='space-y-2'>
                <Label>Sampling Method</Label>
                <Select
                  value={samplingConfig.method}
                  onValueChange={(value) =>
                    setSamplingConfig((prev) => ({
                      ...prev,
                      method: value as SamplingMethod,
                    }))
                  }
                >
                  <SelectTrigger className='w-full text-left'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className='w-[690px] max-w-[690px]'>
                    {(
                      Object.keys(SAMPLING_METHOD_LABELS) as SamplingMethod[]
                    ).map((method) => (
                      <SelectItem key={method} value={method}>
                        <div>
                          <div>{SAMPLING_METHOD_LABELS[method]}</div>
                          <div className='text-muted-foreground w-full text-xs break-all'>
                            {SAMPLING_METHOD_DESCRIPTIONS[method]}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sample Size */}
              {['RANDOM', 'FIXED_COUNT', 'JUDGMENTAL'].includes(
                samplingConfig.method
              ) && (
                <div className='space-y-2'>
                  <Label>Sample Size</Label>
                  <Input
                    type='number'
                    min={1}
                    value={samplingConfig.sampleSize}
                    onChange={(e) =>
                      setSamplingConfig((prev) => ({
                        ...prev,
                        sampleSize: parseInt(e.target.value) || 1,
                      }))
                    }
                  />
                  {template.samplingConfig?.minimumSampleSizeRecommended && (
                    <p className='text-muted-foreground text-xs'>
                      Recommended: at least{' '}
                      {template.samplingConfig.minimumSampleSizeRecommended}{' '}
                      samples
                    </p>
                  )}
                </div>
              )}

              {/* Percentage */}
              {samplingConfig.method === 'FIXED_PERCENT' && (
                <div className='space-y-2'>
                  <Label>Percentage</Label>
                  <div className='flex items-center gap-2'>
                    <Input
                      type='number'
                      min={1}
                      max={100}
                      value={samplingConfig.sampleSize}
                      onChange={(e) =>
                        setSamplingConfig((prev) => ({
                          ...prev,
                          sampleSize: Math.min(
                            100,
                            Math.max(1, parseInt(e.target.value) || 1)
                          ),
                        }))
                      }
                    />
                    <span className='text-muted-foreground text-sm'>%</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className='gap-2'>
            <Button
              variant='outline'
              onClick={() => setSamplingDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSampling}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? 'Creating...' : 'Create Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Browser Profile Configuration Dialog */}
      <Dialog
        open={browserProfileDialogOpen}
        onOpenChange={setBrowserProfileDialogOpen}
      >
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <User className='h-5 w-5 text-purple-600' />
              Configure Identity
            </DialogTitle>
            <DialogDescription>
              {template?.templateType === 'STATIC_SCREENSHOT'
                ? 'This test will automatically capture a screenshot. Select an identity to use for authentication.'
                : 'This workflow will run automatically. Select an identity to use for authentication.'}
            </DialogDescription>
          </DialogHeader>

          {template && (
            <div className='space-y-4 py-2'>
              {/* Template Info */}
              <div className='bg-muted rounded-md p-3'>
                <div className='text-sm font-medium'>
                  {template.title ||
                    template.doc?.topic?.title ||
                    'Untitled Template'}
                </div>
                <div className='text-muted-foreground mt-1 flex items-center gap-1 text-xs'>
                  {template.templateType === 'STATIC_SCREENSHOT' ? (
                    <>
                      <Camera className='h-3 w-3' />
                      Screenshot capture - navigate to URL and take screenshot
                    </>
                  ) : (
                    <>
                      <PlayCircle className='h-3 w-3' />
                      Multi-step workflow execution
                    </>
                  )}
                </div>
              </div>

              {/* Browser Profile Selection */}
              <BrowserProfileSelector
                selectedProfileId={selectedBrowserProfileId}
                onProfileChange={handleBrowserProfileChange}
                showFreshProfile={true}
                label='Select Identity'
                description='Choose an identity with saved login credentials for this test'
              />

              {/* Info Note */}
              <div className='text-muted-foreground rounded-md border border-blue-200 bg-blue-50 p-3 text-xs dark:border-blue-900 dark:bg-blue-950/20'>
                <p>
                  <strong>Note:</strong> You can change the identity or set up
                  scheduling after creating the test from the test detail page.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className='gap-2 sm:gap-0'>
            <Button
              variant='outline'
              onClick={() => setBrowserProfileDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmBrowserProfile}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? 'Creating...' : 'Create Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Main>
  )
}

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/templates/$templateId/'
)({
  component: TemplateDetailPage,
})
